import { z } from 'zod';

/**
 * Network diagnostics.
 *
 * These are dual-use by nature — a port scanner is a port scanner whoever runs
 * it. The controls that keep this an operations tool rather than an attack tool
 * are structural, not cosmetic:
 *
 *  - targets default to private/link-local ranges (RFC1918, CGNAT, ULA); public
 *    addresses require an explicit server-side opt-in;
 *  - sweeps and scans are hard-capped in size, so nobody can point it at a /8;
 *  - the web proxy can only reach resources already registered in the platform,
 *    never a free-form URL, so it cannot become an open SSRF relay;
 *  - every run is audited with the operator and the target.
 */

export const TOOL_KINDS = [
  'ping',
  'traceroute',
  'dns',
  'port-scan',
  'http',
  'neighbours',
  'sweep',
  'lldp',
  'mac-lookup',
] as const;
export type ToolKind = (typeof TOOL_KINDS)[number];

/* ------------------------------------------------------------------ */
/* Streaming envelope                                                  */
/* ------------------------------------------------------------------ */

/**
 * Tools stream NDJSON rather than returning a single blob. A traceroute across
 * 20 hops takes half a minute; showing hops as they resolve is the difference
 * between a usable tool and a spinner.
 */
export type ToolEvent =
  | { type: 'start'; tool: ToolKind; target: string; ts: number }
  /** A raw output line, for the terminal-style transcript. */
  | { type: 'line'; text: string }
  /** One structured datum — a ping reply, a hop, an open port, a live host. */
  | { type: 'item'; item: Record<string, unknown> }
  | { type: 'progress'; done: number; total: number }
  /** Terminal summary. `ok: false` means the tool itself failed. */
  | { type: 'done'; ok: boolean; summary?: Record<string, unknown>; error?: string };

/* ------------------------------------------------------------------ */
/* Target validation                                                   */
/* ------------------------------------------------------------------ */

/**
 * Hostname or IP literal only.
 *
 * Deliberately strict: this string is passed to `ping`/`traceroute` as a
 * process argument. Arguments are never concatenated into a shell here, but a
 * value that cannot contain a metacharacter in the first place removes the
 * whole class of mistake — including the next person who "just adds a shell".
 */
const HOSTNAME = /^(?=.{1,253}$)[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const IPV6 = /^[0-9a-fA-F:]{2,45}$/;

export function isValidTarget(value: string): boolean {
  const v = value.trim();
  if (!v || v.length > 253) return false;
  // A leading dash would be read as a flag by the spawned binary.
  if (v.startsWith('-')) return false;
  return IPV4.test(v) || IPV6.test(v) || HOSTNAME.test(v);
}

export const targetSchema = z
  .string()
  .trim()
  .min(1)
  .max(253)
  .refine(isValidTarget, 'Must be a hostname or IP address');

/** CIDR like 10.0.1.0/24. Prefix floor is enforced separately, per tool. */
export const cidrSchema = z
  .string()
  .trim()
  .regex(/^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}\/([0-9]|[12]\d|3[0-2])$/, 'Must be an IPv4 CIDR, e.g. 10.0.1.0/24');

/* ------------------------------------------------------------------ */
/* Address classification                                              */
/* ------------------------------------------------------------------ */

/**
 * True for addresses inside the ranges an MSP legitimately operates in.
 * Public addresses are refused unless the server explicitly allows them, which
 * is what stops this platform from being pointed at the internet.
 */
/** Is `ip` inside `cidr`? Used by the operator-defined allow-list. */
export function ipInCidr(ip: string, cidr: string): boolean {
  const [base, prefixText] = cidr.split('/');
  if (!base || prefixText === undefined) return false;
  const prefix = Number(prefixText);
  if (!Number.isInteger(prefix) || prefix < 0 || prefix > 32) return false;
  if (!IPV4.test(ip) || !IPV4.test(base)) return false;

  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  return ((ipToInt(ip) & mask) >>> 0) === ((ipToInt(base) & mask) >>> 0);
}

export function isPrivateIPv4(ip: string): boolean {
  const parts = ip.split('.').map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) return false;
  const [a, b] = parts as [number, number, number, number];

  if (a === 10) return true;                       // 10.0.0.0/8
  if (a === 172 && b >= 16 && b <= 31) return true; // 172.16.0.0/12
  if (a === 192 && b === 168) return true;          // 192.168.0.0/16
  if (a === 127) return true;                       // loopback
  if (a === 169 && b === 254) return true;          // link-local
  if (a === 100 && b >= 64 && b <= 127) return true; // CGNAT 100.64.0.0/10
  return false;
}

/* ------------------------------------------------------------------ */
/* Requests                                                            */
/* ------------------------------------------------------------------ */

export const pingRequestSchema = z.object({
  target: targetSchema,
  count: z.number().int().min(1).max(100).default(5),
  timeoutSec: z.number().int().min(1).max(15).default(3),
  /** Larger payloads surface MTU and fragmentation problems. */
  sizeBytes: z.number().int().min(32).max(9000).optional(),
  /**
   * Ping until the operator stops it, rather than `count` times — a live
   * trace for watching a flapping link, where the interesting event is the
   * moment replies start or stop rather than any single round trip.
   */
  continuous: z.boolean().default(false),
});
export type PingRequest = z.infer<typeof pingRequestSchema>;

export const tracerouteRequestSchema = z.object({
  target: targetSchema,
  maxHops: z.number().int().min(1).max(40).default(30),
  timeoutSec: z.number().int().min(1).max(10).default(3),
  /** Skip reverse DNS — much faster, but you lose the hop names. */
  resolveNames: z.boolean().default(true),
});
export type TracerouteRequest = z.infer<typeof tracerouteRequestSchema>;

export const DNS_RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'NS', 'TXT', 'SOA', 'SRV', 'PTR', 'ANY'] as const;

export const dnsRequestSchema = z.object({
  target: targetSchema,
  type: z.enum(DNS_RECORD_TYPES).default('A'),
  /** Query a specific resolver instead of the server's — proves whose DNS is broken. */
  server: targetSchema.optional(),
});
export type DnsRequest = z.infer<typeof dnsRequestSchema>;

/** Named port sets, because nobody remembers that 3389 is RDP mid-incident. */
export const PORT_PRESETS: Record<string, { label: string; ports: number[] }> = {
  common: {
    label: 'Common services',
    ports: [21, 22, 23, 25, 53, 80, 110, 143, 443, 445, 993, 995, 1723, 3306, 3389, 5038, 5060, 5432, 5900, 8080, 8443],
  },
  web: { label: 'Web', ports: [80, 443, 8000, 8008, 8080, 8443, 8888] },
  windows: { label: 'Windows', ports: [135, 139, 389, 445, 636, 3389, 5985, 5986] },
  voip: { label: 'VOIP', ports: [5060, 5061, 5038, 2000, 4569, 8088, 8089] },
  database: { label: 'Databases', ports: [1433, 1521, 3306, 5432, 6379, 27017, 9200] },
  management: { label: 'Management', ports: [22, 23, 161, 443, 623, 8006, 8443, 9090, 10000] },
  // The RouterOS service list, in the order the device itself lists them —
  // scanning these tells you exactly which management planes are exposed.
  mikrotik: { label: 'MikroTik / RouterOS', ports: [21, 22, 23, 80, 443, 2000, 8080, 8291, 8728, 8729] },
};

export const portScanRequestSchema = z.object({
  target: targetSchema,
  /** Explicit list wins; otherwise the preset is used. */
  ports: z.array(z.number().int().min(1).max(65535)).max(1024).optional(),
  preset: z.enum(['common', 'web', 'windows', 'voip', 'database', 'management', 'mikrotik']).default('common'),
  /** Inclusive range. Capped at 1024 ports so this stays a diagnostic. */
  fromPort: z.number().int().min(1).max(65535).optional(),
  toPort: z.number().int().min(1).max(65535).optional(),
  timeoutMs: z.number().int().min(200).max(5000).default(1200),
  concurrency: z.number().int().min(1).max(64).default(24),
  /** Read the service banner from ports that offer one. */
  grabBanner: z.boolean().default(false),
});
export type PortScanRequest = z.infer<typeof portScanRequestSchema>;

export const sweepRequestSchema = z.object({
  cidr: cidrSchema,
  /** /22 is 1022 hosts — already a long scan. Anything wider is a mistake. */
  timeoutMs: z.number().int().min(200).max(5000).default(800),
  concurrency: z.number().int().min(1).max(64).default(32),
  resolveNames: z.boolean().default(true),
  /**
   * Many Windows and appliance hosts drop ICMP but answer TCP. Without this
   * fallback a sweep reports half a live network as empty.
   */
  tcpFallback: z.boolean().default(true),
  fallbackPorts: z.array(z.number().int().min(1).max(65535)).max(8).default([445, 443, 80, 22, 3389]),
});
export type SweepRequest = z.infer<typeof sweepRequestSchema>;

export const lldpRequestSchema = z.object({
  target: targetSchema,
  community: z.string().min(1).max(64).default('public'),
  version: z.enum(['1', '2c']).default('2c'),
});
export type LldpRequest = z.infer<typeof lldpRequestSchema>;

export const httpRequestSchema = z.object({
  url: z.string().url().max(2048),
  method: z.enum(['GET', 'HEAD', 'POST']).default('GET'),
  timeoutMs: z.number().int().min(500).max(30_000).default(10_000),
  insecure: z.boolean().default(true),
  followRedirects: z.boolean().default(true),
});
export type HttpToolRequest = z.infer<typeof httpRequestSchema>;

/**
 * RouterOS interface names — letters, digits, spaces and a few separators.
 * Real ones seen in the field include a dot ("vpn.voipegypt.net") and even a
 * space ("ovpn etiesal"), so neither can be excluded outright. What must be
 * excluded is any script metacharacter: this value is interpolated into a
 * `/tool torch` command sent straight to the router's own CLI, so a value
 * that cannot contain a quote or semicolon in the first place rules out
 * breaking out of its parameter, the same way `targetSchema` rules out a
 * shell metacharacter in a ping target.
 */
const INTERFACE_NAME = /^[a-zA-Z0-9][a-zA-Z0-9 ._-]{0,63}$/;

export const torchRequestSchema = z.object({
  interface: z.string().trim().min(1).max(64).regex(INTERFACE_NAME, 'Must be a valid interface name'),
  durationSec: z.number().int().min(1).max(30).default(5),
});
export type TorchRequest = z.infer<typeof torchRequestSchema>;

/* ------------------------------------------------------------------ */
/* Results                                                             */
/* ------------------------------------------------------------------ */

export interface PingReply {
  seq: number;
  from: string;
  rttMs: number | null;
  ttl: number | null;
  timeout: boolean;
}

export interface TraceHop {
  hop: number;
  host: string | null;
  ip: string | null;
  rttMs: Array<number | null>;
  timeout: boolean;
}

export interface OpenPort {
  port: number;
  open: boolean;
  service: string | null;
  connectMs: number | null;
  banner?: string | null;
  /** Transport that answered; absent means TCP. SIP ports fall back to a UDP
   *  SIP OPTIONS when TCP is closed, since most SIP only listens on UDP. */
  proto?: 'tcp' | 'udp';
  /** Which of the resource's addresses this was scanned on — set only when a
   *  resource has more than one; absent means "the primary address". */
  host?: string;
  addressLabel?: string | null;
}

export interface DiscoveredHost {
  ip: string;
  alive: boolean;
  rttMs: number | null;
  hostname: string | null;
  mac: string | null;
  vendor: string | null;
  /** Which probe found it — ICMP, or the TCP port that answered. */
  via: 'icmp' | 'tcp' | 'arp';
  openPorts?: number[];
}

export interface Neighbour {
  ip: string;
  mac: string;
  vendor: string | null;
  /** ARP interface or LLDP local port. */
  iface: string | null;
  state: string | null;
  hostname?: string | null;
}

export interface LldpNeighbour {
  localPort: string;
  remoteName: string | null;
  remotePort: string | null;
  remoteDescription: string | null;
  remoteChassisId: string | null;
  managementAddress: string | null;
}

/** One flow from a RouterOS Torch capture — bytes/packets seen during the run. */
export interface TorchFlow {
  srcAddress: string | null;
  srcPort: number | null;
  dstAddress: string | null;
  dstPort: number | null;
  /** The name RouterOS resolves a well-known port to, e.g. "https" for 443. */
  service: string | null;
  protocol: string | null;
  txBytes: number | null;
  rxBytes: number | null;
  txPackets: number | null;
  rxPackets: number | null;
}

export const mikrotikUsersQuerySchema = z.object({
  windowSec: z.coerce.number().int().min(2).max(10).default(3),
});

/**
 * One user behind a MikroTik — a DHCP lease, an ARP entry, a hotspot login or
 * just a LAN address seen in the connection table — and what it is pulling
 * from the internet right now.
 */
export interface MikrotikUser {
  address: string;
  mac: string | null;
  /** Hotspot user name, else the lease or ARP comment, else the DHCP host name. */
  name: string | null;
  hostName: string | null;
  vendor: string | null;
  interface: string | null;
  sources: Array<'dhcp' | 'arp' | 'hotspot' | 'traffic'>;
  /** Internet traffic only: towards the user, and from it. */
  downloadBps: number;
  uploadBps: number;
  /** Open internet connections. */
  connections: number;
  /** Hotspot session counters, when the user is logged in to the hotspot. */
  sessionDownloadBytes: number | null;
  sessionUploadBytes: number | null;
  sessionUptime: string | null;
}

export interface MikrotikUsersResult {
  ts: number;
  windowSec: number;
  users: MikrotikUser[];
  totalDownloadBps: number;
  totalUploadBps: number;
  /** The same window read off the internet ports' own counters, for comparison. */
  wanDownloadBps: number | null;
  wanUploadBps: number | null;
  wanInterfaces: string[];
  lanNetworks: string[];
  connectionsSeen: number;
}

/** Well-known service names for the ports the scanner reports. */
export const SERVICE_NAMES: Record<number, string> = {
  21: 'ftp', 22: 'ssh', 23: 'telnet', 25: 'smtp', 53: 'dns', 67: 'dhcp', 69: 'tftp',
  80: 'http', 88: 'kerberos', 110: 'pop3', 123: 'ntp', 135: 'msrpc', 137: 'netbios',
  139: 'netbios-ssn', 143: 'imap', 161: 'snmp', 162: 'snmp-trap', 389: 'ldap',
  443: 'https', 445: 'smb', 465: 'smtps', 514: 'syslog', 587: 'submission',
  623: 'ipmi', 636: 'ldaps', 993: 'imaps', 995: 'pop3s', 1433: 'mssql',
  1521: 'oracle', 1723: 'pptp', 2000: 'sccp', 3128: 'squid', 3306: 'mysql',
  3389: 'rdp', 4569: 'iax2', 5038: 'asterisk-ami', 5060: 'sip', 5061: 'sip-tls',
  5432: 'postgres', 5900: 'vnc', 5985: 'winrm', 5986: 'winrm-tls', 6379: 'redis',
  8000: 'http-alt', 8006: 'proxmox', 8080: 'http-proxy', 8088: 'asterisk-http',
  8291: 'winbox', 8443: 'https-alt', 8728: 'mikrotik-api', 8729: 'mikrotik-api-ssl',
  9090: 'cockpit', 9200: 'elasticsearch', 10000: 'webmin',
  27017: 'mongodb',
};

export function serviceName(port: number): string | null {
  return SERVICE_NAMES[port] ?? null;
}

/* ------------------------------------------------------------------ */
/* CIDR helpers                                                        */
/* ------------------------------------------------------------------ */

export function ipToInt(ip: string): number {
  return ip.split('.').reduce((acc, octet) => (acc << 8) + Number(octet), 0) >>> 0;
}

export function intToIp(value: number): string {
  return [(value >>> 24) & 255, (value >>> 16) & 255, (value >>> 8) & 255, value & 255].join('.');
}

/**
 * Usable host addresses in a CIDR.
 *
 * Network and broadcast are excluded for /30 and wider; /31 and /32 return
 * every address, since point-to-point links legitimately use both.
 */
export function cidrHosts(cidr: string, maxHosts = 4096): { hosts: string[]; truncated: boolean } {
  const [base, prefixText] = cidr.split('/') as [string, string];
  const prefix = Number(prefixText);
  const baseInt = ipToInt(base);
  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  const network = (baseInt & mask) >>> 0;
  const size = 2 ** (32 - prefix);

  const first = prefix >= 31 ? network : network + 1;
  const last = prefix >= 31 ? network + size - 1 : network + size - 2;

  const hosts: string[] = [];
  let truncated = false;
  for (let i = first; i <= last; i++) {
    if (hosts.length >= maxHosts) {
      truncated = true;
      break;
    }
    hosts.push(intToIp(i));
  }
  return { hosts, truncated };
}

/**
 * MAC OUI → vendor, for the manufacturers that actually turn up in an MSP's
 * estate. This is a curated subset, not the full IEEE registry — an unknown
 * prefix returns null rather than a wrong guess.
 */
export const OUI_VENDORS: Record<string, string> = {
  '00:0C:29': 'VMware', '00:50:56': 'VMware', '00:05:69': 'VMware', '00:1C:14': 'VMware',
  '08:00:27': 'VirtualBox', '52:54:00': 'QEMU/KVM', '00:15:5D': 'Microsoft Hyper-V',
  '00:16:3E': 'Xen', '00:1A:4A': 'Qumranet',
  '00:00:0C': 'Cisco', '00:1B:0D': 'Cisco', '00:1E:14': 'Cisco', '00:26:0B': 'Cisco',
  'F4:CF:E2': 'Cisco', '00:0A:F7': 'Broadcom',
  '00:09:0F': 'Fortinet', '08:5B:0E': 'Fortinet', '90:6C:AC': 'Fortinet',
  '00:1B:17': 'Palo Alto Networks', 'B4:0C:25': 'Palo Alto Networks',
  '00:0C:42': 'MikroTik', '4C:5E:0C': 'MikroTik', '48:8F:5A': 'MikroTik', '2C:C8:1B': 'MikroTik',
  '00:1D:AA': 'DrayTek', '00:50:7F': 'DrayTek',
  '00:E0:4C': 'Realtek', '00:1F:29': 'HP', '00:25:B3': 'HP', '3C:D9:2B': 'HP',
  '00:21:5A': 'HP', 'B4:B5:2F': 'HP', '00:0B:86': 'Aruba', '6C:F3:7F': 'Aruba',
  '00:17:88': 'Philips Hue', '00:04:F2': 'Polycom', '00:E0:BB': 'Polycom',
  '00:0B:82': 'Grandstream', 'C0:74:AD': 'Grandstream',
  '00:15:65': 'Yealink', '80:5E:C0': 'Yealink', '24:9A:D8': 'Yealink',
  '00:04:13': 'SNOM', '00:1F:9F': 'Sagemcom',
  'DC:A6:32': 'Raspberry Pi', 'B8:27:EB': 'Raspberry Pi', 'E4:5F:01': 'Raspberry Pi',
  '00:1D:0F': 'TP-Link', '50:C7:BF': 'TP-Link', 'AC:84:C6': 'TP-Link',
  '00:18:4D': 'Netgear', '20:4E:7F': 'Netgear', 'A0:40:A0': 'Netgear',
  '00:24:01': 'D-Link', '00:26:5A': 'D-Link',
  '00:1C:B3': 'Apple', '00:25:00': 'Apple', 'AC:BC:32': 'Apple', 'F0:18:98': 'Apple',
  '00:03:93': 'Apple', '00:23:12': 'Apple',
  '00:1A:11': 'Google', '3C:5A:B4': 'Google', 'F4:F5:D8': 'Google',
  '00:12:17': 'Cisco-Linksys', '00:1E:58': 'D-Link',
  '00:07:7D': 'Cisco', '00:D0:2B': 'Cisco',
  'FC:EC:DA': 'Ubiquiti', '24:5A:4C': 'Ubiquiti', '78:8A:20': 'Ubiquiti', '04:18:D6': 'Ubiquiti',
  '00:27:22': 'Ubiquiti', 'DC:9F:DB': 'Ubiquiti',
  '00:13:3B': 'Speedway', '00:1F:33': 'Netgear',
  '00:90:0B': 'Lanner', '00:60:E0': 'Axiom',
  '00:11:32': 'Synology', '00:1B:21': 'Intel', '00:1E:67': 'Intel', 'A4:BF:01': 'Intel',
  '60:A5:E2': 'Intel', '2C:6D:C1': 'Intel', '38:87:D5': 'Intel',
  '00:E0:4B': 'ZyXEL', '5C:F4:AB': 'ZyXEL',
  '00:24:8C': 'ASUSTek', '2C:56:DC': 'ASUSTek', '1C:B7:2C': 'ASUSTek',
  'B8:AE:ED': 'Elitegroup', '00:19:99': 'Fujitsu', '00:1E:0B': 'HP',
  '9C:7B:EF': 'HP', '64:51:06': 'HP',
  '00:14:22': 'Dell', '00:1E:C9': 'Dell', 'B0:83:FE': 'Dell', '18:66:DA': 'Dell',
  '00:21:9B': 'Dell', 'F8:BC:12': 'Dell', '00:26:B9': 'Dell',
  'F8:B1:56': 'Dell', '64:00:6A': 'Dell', 'C0:25:A5': 'Dell', '74:86:E2': 'Dell',
  // Registered to Kyocera's display division, but on an office LAN a Kyocera
  // MAC is a printer/MFP — confirmed against real devices on this estate.
  '00:17:C8': 'Kyocera',

  // Networking, beyond the core set above.
  'E4:F2:7C': 'Juniper Networks', '60:C7:8D': 'Juniper Networks', '3C:08:CD': 'Juniper Networks',
  '28:C0:DA': 'Juniper Networks', '00:21:59': 'Juniper Networks',
  'E0:06:30': 'Huawei', 'D8:DA:F1': 'Huawei', '54:44:3B': 'Huawei', '5C:70:75': 'Huawei', '78:2D:AD': 'Huawei',
  '88:7E:25': 'Extreme Networks', 'B8:7C:F2': 'Extreme Networks', 'F4:EA:B5': 'Extreme Networks',
  '08:EA:44': 'Extreme Networks', 'BC:34:D6': 'Extreme Networks',
  '9C:E3:30': 'Cisco Meraki', 'B4:DF:91': 'Cisco Meraki', 'B8:AB:61': 'Cisco Meraki',
  '08:F1:B3': 'Cisco Meraki', 'CC:9C:3E': 'Cisco Meraki',
  'D4:BD:4F': 'Ruckus Wireless', '5C:DF:89': 'Ruckus Wireless', '38:45:3B': 'Ruckus Wireless',
  '60:D0:2C': 'Ruckus Wireless', '34:FA:9F': 'Ruckus Wireless',
  '6C:4F:89': 'ZyXEL', '30:15:77': 'ZyXEL', '88:F3:D5': 'ZyXEL', 'F4:4D:5C': 'ZyXEL', '78:C5:7D': 'ZyXEL',
  '00:1A:1E': 'Aruba', 'D8:C7:C8': 'Aruba', 'AC:A3:1E': 'Aruba',
  '4C:60:DE': 'Netgear', '08:BD:43': 'Netgear', '28:94:01': 'Netgear', '84:1B:5E': 'Netgear', 'C0:FF:D4': 'Netgear',
  '0C:EA:14': 'Ubiquiti', '2C:E5:BD': 'Ubiquiti', '78:45:58': 'Ubiquiti', 'AC:8B:A9': 'Ubiquiti', '9C:05:D6': 'Ubiquiti',

  // VoIP / telephony.
  '3C:B1:5B': 'Avaya', 'C8:1F:EA': 'Avaya', '34:75:C7': 'Avaya', 'B0:AD:AA': 'Avaya', 'B4:47:5E': 'Avaya',
  'BC:C3:42': 'Panasonic', '00:80:F0': 'Panasonic', '24:78:23': 'Panasonic',
  '0C:38:3E': 'Fanvil', '00:1F:C1': 'Htek',
  'E8:0A:B9': 'Cisco', '48:1B:A4': 'Cisco', '6C:03:B5': 'Cisco',
  // A distinct label from 'Cisco-Linksys' on purpose — that name already means
  // "home router" to guessKindFromVendor below, and these SPA-series OUIs are
  // desk phones, not routers.
  '00:1D:7E': 'Cisco (SPA phones)', '00:14:BF': 'Cisco (SPA phones)',
  '08:00:0F': 'Mitel', '14:00:E9': 'Mitel', '00:08:5D': 'Mitel',
  '00:60:3D': '3CX', '00:17:19': 'AudioCodes', '00:05:E7': 'AudioCodes',

  // IP cameras / NVR / DVR.
  '0C:75:D2': 'Hikvision', '54:8C:81': 'Hikvision', '24:48:45': 'Hikvision', 'EC:C8:9C': 'Hikvision', '8C:E7:48': 'Hikvision',
  '74:C9:29': 'Dahua', '6C:1C:71': 'Dahua', '08:ED:ED': 'Dahua', 'E0:2E:FE': 'Dahua', '5C:F5:1A': 'Dahua',
  'B8:A4:4F': 'Axis Communications', '00:40:8C': 'Axis Communications', 'E8:27:25': 'Axis Communications', 'AC:CC:8E': 'Axis Communications',
  '88:26:3F': 'Uniview', '14:BA:88': 'Uniview', '6C:F1:7E': 'Uniview', '48:EA:63': 'Uniview', 'C4:79:05': 'Uniview',
  'EC:71:DB': 'Reolink',
  '00:65:1E': 'Amcrest', '9C:8E:CD': 'Amcrest', 'A0:60:32': 'Amcrest',

  // Compute / servers.
  '10:C5:95': 'Lenovo', 'A4:11:94': 'Lenovo', '48:C3:5A': 'Lenovo', '80:96:21': 'Lenovo',
  '0C:C4:7A': 'Supermicro', '90:5A:08': 'Supermicro', 'AC:1F:6B': 'Supermicro', '00:25:90': 'Supermicro',
  '00:50:76': 'IBM', '74:99:75': 'IBM', '6C:AE:8B': 'IBM', '08:17:F4': 'IBM',
  '14:02:EC': 'Hewlett Packard Enterprise', '1C:98:EC': 'Hewlett Packard Enterprise',
  '24:F2:7F': 'Hewlett Packard Enterprise', '94:18:82': 'Hewlett Packard Enterprise',
  '88:25:10': 'Hewlett Packard Enterprise',

  // Storage.
  '24:5E:BE': 'QNAP', 'E8:43:B6': 'QNAP', '90:09:D0': 'Synology',

  // Printers.
  '84:BA:3B': 'Canon', '60:12:8B': 'Canon', '5C:62:5A': 'Canon', '74:38:B7': 'Canon',
  'A4:D7:3C': 'Epson', '50:57:9C': 'Epson', '00:26:AB': 'Epson', '58:05:D9': 'Epson',
  '00:80:77': 'Brother', 'B0:7C:8E': 'Brother', '3C:2A:F4': 'Brother', '94:DD:F8': 'Brother',
  '9C:93:4E': 'Xerox', 'E8:4D:EC': 'Xerox',

  // Consumer / IoT.
  '78:28:CA': 'Sonos', '54:2A:1B': 'Sonos', 'F0:F6:C1': 'Sonos', '60:F6:20': 'Sonos',
  '84:28:59': 'Amazon', '28:73:F6': 'Amazon', 'E0:CB:1D': 'Amazon', 'FC:D7:49': 'Amazon',
  '64:1B:2F': 'Samsung', '9C:73:B1': 'Samsung', '38:8A:06': 'Samsung', '48:BC:E1': 'Samsung',
  '04:B9:E3': 'Samsung',
  'CC:EB:5E': 'Xiaomi', 'B8:EA:98': 'Xiaomi', '8C:D0:B2': 'Xiaomi', 'F4:1A:9C': 'Xiaomi',
};

export function macVendor(mac: string): string | null {
  const prefix = mac.toUpperCase().replace(/-/g, ':').split(':').slice(0, 3).join(':');
  return OUI_VENDORS[prefix] ?? null;
}

/**
 * True for a MAC with the "locally administered" bit set — the second-lowest
 * bit of the first octet. Every modern phone/laptop OS (iOS 14+, Android 10+,
 * Windows 10+) generates one of these per network by default for Wi-Fi
 * privacy, so it is not assigned to any manufacturer and never will be — no
 * amount of OUI table coverage can name its vendor. Worth telling apart from
 * a genuine gap in the vendor table, which *could* be filled in.
 */
export function isLocallyAdministeredMac(mac: string): boolean {
  const firstByte = parseInt(mac.slice(0, 2), 16);
  return Number.isFinite(firstByte) && (firstByte & 0x02) !== 0;
}

export interface ResourceKindGuess {
  pluginId: string;
  kind: string;
  label: string;
}

/**
 * Vendor -> resource kind, for the handful of OUI vendors that mean only one
 * thing in practice. Deliberately narrow — a vendor like Cisco or TP-Link
 * turns up on routers, switches and APs alike, so guessing wrong there would
 * cost more (a silently mis-typed resource) than it saves; every kind below
 * only needs a `host` to create, so the guess never leaves a resource with
 * unmet required config. A wrong guess elsewhere is a one-click "Convert
 * type" away, not a dead end.
 */
const VENDOR_KIND: Record<string, ResourceKindGuess> = {
  MikroTik: { pluginId: 'routers', kind: 'router', label: 'Router' },
  DrayTek: { pluginId: 'routers', kind: 'router', label: 'Router' },
  'Cisco-Linksys': { pluginId: 'routers', kind: 'router', label: 'Router' },
  Fortinet: { pluginId: 'firewalls', kind: 'firewall', label: 'Firewall' },
  'Palo Alto Networks': { pluginId: 'firewalls', kind: 'firewall', label: 'Firewall' },
  Ubiquiti: { pluginId: 'networks', kind: 'access-point', label: 'Wireless access point' },
  // VoIP handsets — these vendors ship desk phones almost exclusively, unlike
  // a Cisco or Grandstream OUI which also turns up on gateways and ATAs.
  Polycom: { pluginId: 'voip', kind: 'ip-phone', label: 'IP phone' },
  Yealink: { pluginId: 'voip', kind: 'ip-phone', label: 'IP phone' },
  SNOM: { pluginId: 'voip', kind: 'ip-phone', label: 'IP phone' },
  Fanvil: { pluginId: 'voip', kind: 'ip-phone', label: 'IP phone' },
  Htek: { pluginId: 'voip', kind: 'ip-phone', label: 'IP phone' },
  // Surveillance vendors — camera vs. NVR/DVR shares the same OUI and can't be
  // told apart from the MAC alone, but a camera is the far more common count
  // per site, so it's the default; wrong is one "Convert type" away.
  Hikvision: { pluginId: 'office-devices', kind: 'ip-camera', label: 'IP camera' },
  Dahua: { pluginId: 'office-devices', kind: 'ip-camera', label: 'IP camera' },
  Uniview: { pluginId: 'office-devices', kind: 'ip-camera', label: 'IP camera' },
  Reolink: { pluginId: 'office-devices', kind: 'ip-camera', label: 'IP camera' },
  Amcrest: { pluginId: 'office-devices', kind: 'ip-camera', label: 'IP camera' },
  'Axis Communications': { pluginId: 'office-devices', kind: 'ip-camera', label: 'IP camera' },
  // Printer-only vendors.
  Canon: { pluginId: 'office-devices', kind: 'printer', label: 'Network printer' },
  Epson: { pluginId: 'office-devices', kind: 'printer', label: 'Network printer' },
  Brother: { pluginId: 'office-devices', kind: 'printer', label: 'Network printer' },
  Xerox: { pluginId: 'office-devices', kind: 'printer', label: 'Network printer' },
  Kyocera: { pluginId: 'office-devices', kind: 'printer', label: 'Network printer' },
  // Computer makers and NIC/board vendors. Unlike HP or Samsung these sell
  // almost nothing else that lands on an office LAN, so the vendor alone is a
  // fair "this is a computer" — a printer or phone from one is a "Convert
  // type" away.
  Dell: { pluginId: 'computers', kind: 'workstation', label: 'Computer' },
  Intel: { pluginId: 'computers', kind: 'workstation', label: 'Computer' },
  Gigabyte: { pluginId: 'computers', kind: 'workstation', label: 'Computer' },
  'Micro-Star': { pluginId: 'computers', kind: 'workstation', label: 'Computer' },
  ASUSTek: { pluginId: 'computers', kind: 'workstation', label: 'Computer' },
  Lenovo: { pluginId: 'computers', kind: 'workstation', label: 'Computer' },
  Acer: { pluginId: 'computers', kind: 'workstation', label: 'Computer' },
  Elitegroup: { pluginId: 'computers', kind: 'workstation', label: 'Computer' },
  // Handset makers. There is no phone resource kind, so these file as a
  // computer when added, but read as what they are in the list.
  Samsung: { pluginId: 'computers', kind: 'workstation', label: 'Mobile device' },
  Honor: { pluginId: 'computers', kind: 'workstation', label: 'Mobile device' },
  OPPO: { pluginId: 'computers', kind: 'workstation', label: 'Mobile device' },
  vivo: { pluginId: 'computers', kind: 'workstation', label: 'Mobile device' },
  OnePlus: { pluginId: 'computers', kind: 'workstation', label: 'Mobile device' },
  // A ZTE MAC on an office LAN is nearly always the ISP's CPE/ONT.
  ZTE: { pluginId: 'networks', kind: 'modem', label: 'Modem / CPE' },
  'Philips Hue': { pluginId: 'office-devices', kind: 'iot-device', label: 'IoT device' },
  Sonos: { pluginId: 'office-devices', kind: 'iot-device', label: 'IoT device' },
  // A NAS is a specialised Linux box — the closest kind this platform has.
  Synology: { pluginId: 'servers', kind: 'linux-server', label: 'Linux server' },
  QNAP: { pluginId: 'servers', kind: 'linux-server', label: 'Linux server' },
  // A single-board computer's actual role varies too much to guess further,
  // but it is always a small Linux machine, never networking gear.
  'Raspberry Pi': { pluginId: 'computers', kind: 'workstation', label: 'Workstation' },
  'Extreme Networks': { pluginId: 'networks', kind: 'switch', label: 'Switch' },
  'Ruckus Wireless': { pluginId: 'networks', kind: 'access-point', label: 'Wireless access point' },
};

export function guessKindFromVendor(vendor: string | null): ResourceKindGuess | null {
  return vendor ? (VENDOR_KIND[vendor] ?? null) : null;
}

const MAC_RE = /^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/;

export function isValidMac(value: string): boolean {
  return MAC_RE.test(value.trim());
}
